import React, { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Heart, Palette, Shield, Users, Compass, Feather, Activity, Droplet,
  Plus, TrendingUp, Star, ChevronDown, ChevronUp, Sparkles, Share2,
} from "lucide-react";

const ICON_BY_SLUG = {
  emotional:   Heart,
  creative:    Palette,
  boundaries:  Shield,
  connection:  Users,
  goals:       Compass,
  weight:      Feather,
  fitness:     Activity,
  health:      Droplet,
};

export default function LongevityMilestones({ isPro, onEnablePro }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(null); // slug currently expanded for editing
  // Chosen goal-tag slugs — the "areas to nurture" the user is choosing.
  // Persists to profile.longevity_goals; single source of truth for the whole app.
  const [goalTags, setGoalTags] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [savingChoice, setSavingChoice] = useState(false);

  const load = async () => {
    // Fetch the goal tag catalog + current profile selection alongside milestones.
    const meta = await endpoints.meta().catch(() => ({ data: {} }));
    const prof = await endpoints.profile().catch(() => ({ data: {} }));
    setGoalTags(meta.data?.goal_tags || []);
    setChosen(prof.data?.longevity_goals || []);

    if (!isPro) {
      const { data } = await endpoints.milestoneCategories();
      setRows(data.map((c) => ({ ...c, milestone: null })));
      setLoaded(true);
      return;
    }
    try {
      const { data } = await endpoints.milestones();
      setRows(data);
    } catch {
      /* silent */
    }
    setLoaded(true);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [isPro]);

  const toggleGoal = async (slug) => {
    const on = chosen.includes(slug);
    const next = on ? chosen.filter((x) => x !== slug) : [...chosen, slug];
    setChosen(next);
    setSavingChoice(true);
    try {
      await endpoints.updateProfile({ longevity_goals: next });
    } catch {
      // Rollback on failure so the UI never drifts from server truth.
      setChosen(chosen);
      toast.error("Couldn't save that change.");
    } finally {
      setSavingChoice(false);
    }
  };

  if (!loaded) return null;

  return (
    <section
      className="p-8 border hairline rounded-md bg-[hsl(var(--paper))] reveal reveal-3"
      data-testid="longevity-milestones"
    >
      {/* Goal chooser — every member (Free or Pro) can pick which areas of
          life they're choosing to nurture. Single source of truth for
          longevity_goals across N8ONE. */}
      <div data-testid="goal-tag-chooser">
        <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--muted-foreground))]">
          Choose your areas
        </div>
        <p className="mt-1 text-sm text-[hsl(var(--muted-foreground))] leading-relaxed max-w-2xl">
          Tap the areas of life you're choosing to nurture. N8 uses these to shape everything —
          rituals, reflections, and the practices that come to you.
        </p>
        <div className="mt-4 flex flex-wrap gap-2" data-testid="goal-tag-chooser-list">
          {goalTags.map((g) => {
            const on = chosen.includes(g.slug);
            return (
              <button
                key={g.slug}
                type="button"
                onClick={() => toggleGoal(g.slug)}
                data-testid={`goal-tag-${g.slug}`}
                disabled={savingChoice}
                className={`px-3.5 py-1.5 rounded-full text-sm border hairline tab-link ${
                  on
                    ? "bg-[hsl(var(--moss))] text-[hsl(var(--paper))] border-transparent"
                    : "hover:bg-[hsl(var(--sand))]"
                } disabled:opacity-60`}
              >
                {g.name}
              </button>
            );
          })}
        </div>
      </div>

      {!isPro && (
        <div className="mt-8 p-5 border hairline rounded-md bg-[hsl(var(--terracotta))]/10" data-testid="milestones-upsell">
          <div className="flex items-center gap-4 flex-wrap">
            <p className="flex-1 min-w-0 font-display text-xl italic">
              Set and track goals across emotional, creative, boundaries, connection, goals, weight, fitness and optimal health.
            </p>
            <Button
              onClick={onEnablePro}
              data-testid="milestones-try-pro"
              className="rounded-full bg-[hsl(var(--terracotta))] hover:bg-[hsl(var(--terracotta))]/90 text-[hsl(var(--fg))]"
            >
              <Sparkles size={14} className="mr-2" /> Try Pro
            </Button>
          </div>
        </div>
      )}

      <details className="group mt-8" data-testid="milestones-grid-details" open>
        <summary
          className="list-none cursor-pointer flex items-center justify-between gap-3 px-4 py-3 rounded-md border hairline bg-[hsl(var(--sand))]/30 hover:bg-[hsl(var(--sand))]/50 tab-link"
          data-testid="milestones-grid-summary"
        >
          <span className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
            {isPro ? "Your milestones · tap to view" : "Explore each area · tap to view"}
          </span>
          <ChevronDown
            size={14}
            className="text-[hsl(var(--muted-foreground))] transition-transform group-open:rotate-180"
            aria-hidden="true"
          />
        </summary>
        <div className={`mt-4 grid gap-4 ${isPro ? "" : "opacity-60 pointer-events-none"}`} data-testid="milestones-grid">
          {rows.map((row) => (
            <MilestoneCard
              key={row.slug}
              row={row}
              expanded={expanded === row.slug}
              onExpand={() => setExpanded(expanded === row.slug ? null : row.slug)}
              onSaved={load}
            />
          ))}
        </div>
      </details>

      {/* Fine-tune each area — deeper, N8-Guide-facing specifics per pillar.
          Moved from Profile so every goal lives in one place. */}
      <LifeAreaDetails />
    </section>
  );
}

/**
 * LifeAreaDetails — collapsible per-pillar chip grids that feed N8 Guide's
 * daily prompt. Skin, Fitness, Nutrition & Hormones, Emotional, Sleep,
 * Mindset. Kept collapsed by default so the primary "areas" chooser above
 * stays the top of mind, and every specific tag is one tap away.
 */
function LifeAreaDetails() {
  const [meta, setMeta] = useState({});
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const [m, p] = await Promise.all([
        endpoints.meta().catch(() => ({ data: {} })),
        endpoints.profile().catch(() => ({ data: {} })),
      ]);
      setMeta(m.data || {});
      setProfile(p.data || {});
    })();
  }, []);

  if (!profile) return null;

  const list = (field) => Array.isArray(profile[field]) ? profile[field] : [];

  const toggle = async (field, slug) => {
    const cur = list(field);
    const on = cur.includes(slug);
    const next = on ? cur.filter((x) => x !== slug) : [...cur, slug];
    const optimistic = { ...profile, [field]: next };
    setProfile(optimistic);
    setSaving(true);
    try {
      await endpoints.updateProfile({ [field]: next });
    } catch {
      setProfile(profile);
      toast.error("Couldn't save that change.");
    } finally {
      setSaving(false);
    }
  };

  const groups = [
    { field: "skin_goals",      options: meta.skin_goal_tags,      label: "Skin care" },
    { field: "fitness_goals",   options: meta.fitness_goal_tags,   label: "Fitness" },
    { field: "nutrition_goals", options: meta.nutrition_goal_tags, label: "Nutrition & hormones" },
    { field: "emotional_goals", options: meta.emotional_goal_tags, label: "Emotional wellbeing" },
    { field: "sleep_goals",     options: meta.sleep_goal_tags,     label: "Sleep" },
    { field: "mindset_goals",   options: meta.mindset_goal_tags,   label: "Mindset" },
  ].filter((g) => Array.isArray(g.options) && g.options.length > 0);

  return (
    <details className="group mt-6" data-testid="life-area-details" open={false}>
      <summary
        className="list-none cursor-pointer flex items-center justify-between gap-3 px-4 py-3 rounded-md border hairline bg-[hsl(var(--sand))]/30 hover:bg-[hsl(var(--sand))]/50 tab-link"
        data-testid="life-area-details-summary"
      >
        <span className="text-[11px] uppercase tracking-[0.22em] text-[hsl(var(--muted-foreground))]">
          Fine-tune each area · tap to view
        </span>
        <ChevronDown
          size={14}
          className="text-[hsl(var(--muted-foreground))] transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
      </summary>

      <div className="mt-5 space-y-7">
        {groups.map((g) => (
          <div key={g.field} data-testid={`life-area-group-${g.field}`}>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[hsl(var(--copper))]">
              {g.label}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {g.options.map((opt) => {
                const on = list(g.field).includes(opt.slug);
                return (
                  <button
                    key={opt.slug}
                    type="button"
                    disabled={saving}
                    onClick={() => toggle(g.field, opt.slug)}
                    data-testid={`life-area-${g.field}-${opt.slug}`}
                    className={`px-3.5 py-1.5 rounded-full text-sm border hairline tab-link ${
                      on
                        ? "bg-[hsl(var(--clay))] text-[hsl(var(--paper))] border-transparent"
                        : "hover:bg-[hsl(var(--sand))]"
                    } disabled:opacity-60`}
                  >
                    {opt.name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </details>
  );
}

function MilestoneCard({ row, expanded, onExpand, onSaved }) {
  const Icon = ICON_BY_SLUG[row.slug] || Star;
  const m = row.milestone;
  const hasProgress = m && Array.isArray(m.progress) && m.progress.length > 0;
  const pct = (m && m.target_value && m.current_value != null)
    ? Math.min(100, Math.max(0, Math.round((m.current_value / m.target_value) * 100)))
    : null;

  return (
    <article className="border hairline rounded-md bg-[hsl(var(--paper))] overflow-hidden" data-testid={`milestone-${row.slug}`}>
      <button
        onClick={onExpand}
        data-testid={`milestone-toggle-${row.slug}`}
        className="w-full text-left px-5 py-4 flex items-center gap-4 hover:bg-[hsl(var(--sand))]/30"
      >
        <span className="shrink-0 w-9 h-9 rounded-full bg-[hsl(var(--sand))]/60 grid place-items-center text-[hsl(var(--clay))]">
          <Icon size={16} strokeWidth={1.7} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="font-display text-xl leading-tight">{row.name}</div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))] mt-1 truncate">
            {m?.target ? m.target : row.prompt}
          </div>
        </div>
        {pct !== null && (
          <div className="w-20 shrink-0 text-right" aria-hidden data-testid={`milestone-pct-${row.slug}`}>
            <div className="font-display text-lg leading-none tabular-nums">{pct}%</div>
            <div className="mt-1 w-full h-1.5 rounded-full bg-[hsl(var(--sand))] overflow-hidden">
              <div className="h-full bg-[hsl(var(--terracotta))] transition-[width] duration-500" style={{ width: `${pct}%` }} />
            </div>
          </div>
        )}
        {expanded ? <ChevronUp size={16} className="text-[hsl(var(--muted-foreground))]" /> : <ChevronDown size={16} className="text-[hsl(var(--muted-foreground))]" />}
      </button>

      {expanded && (
        <MilestoneEditor row={row} onSaved={onSaved} />
      )}
    </article>
  );
}

function MilestoneEditor({ row, onSaved }) {
  const m = row.milestone || {};
  const [description, setDescription] = useState(m.description || "");
  const [target, setTarget] = useState(m.target || "");
  const [targetValue, setTargetValue] = useState(m.target_value ?? "");
  const [currentValue, setCurrentValue] = useState(m.current_value ?? "");
  const [unit, setUnit] = useState(m.unit || "");
  const [targetDate, setTargetDate] = useState(m.target_date || "");
  const [saving, setSaving] = useState(false);
  const [logOpen, setLogOpen] = useState(false);
  const [logValue, setLogValue] = useState("");
  const [logNote, setLogNote] = useState("");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareNote, setShareNote] = useState("");
  const [sharing, setSharing] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await endpoints.upsertMilestone(row.slug, {
        description: description.trim(),
        target: target.trim(),
        target_value: targetValue === "" ? null : Number(targetValue),
        current_value: currentValue === "" ? null : Number(currentValue),
        unit: unit.trim(),
        target_date: targetDate.trim() || null,
      });
      toast.success("Milestone saved.");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save.");
    }
    setSaving(false);
  };

  const logProgress = async () => {
    try {
      await endpoints.addMilestoneProgress(row.slug, {
        value: logValue === "" ? null : Number(logValue),
        note: logNote.trim(),
      });
      toast.success("Progress logged.");
      setLogValue("");
      setLogNote("");
      setLogOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not log.");
    }
  };

  const shareToCommunity = async () => {
    setSharing(true);
    try {
      await endpoints.shareMilestone(row.slug, { note: shareNote.trim() });
      toast.success("Shared to the feed. May your circle hold you.");
      setShareNote("");
      setShareOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not share.");
    }
    setSharing(false);
  };

  const recent = ((m.progress || []).slice(-5)).reverse();

  return (
    <div className="px-5 pb-5 pt-1 border-t hairline space-y-4" data-testid={`milestone-editor-${row.slug}`}>
      <Field label="What you are moving toward">
        <Textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={row.prompt}
          data-testid={`milestone-description-${row.slug}`}
          className="min-h-[70px] bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--clay))]"
        />
      </Field>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Target (freeform)">
          <Textarea
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder={row.target_placeholder || row.prompt}
            data-testid={`milestone-target-${row.slug}`}
            className="min-h-[110px] bg-transparent border-[hsl(var(--border))] focus-visible:ring-[hsl(var(--clay))] resize-y text-[15px] leading-relaxed"
          />
        </Field>
        <Field label="Target date (optional)">
          <Input
            value={targetDate}
            onChange={(e) => setTargetDate(e.target.value)}
            placeholder="YYYY-MM-DD"
            data-testid={`milestone-target-date-${row.slug}`}
            className="bg-transparent"
          />
        </Field>
        {row.numeric && (
          <>
            <Field label="Target value (optional)">
              <Input
                type="number"
                step="any"
                value={targetValue}
                onChange={(e) => setTargetValue(e.target.value)}
                placeholder="e.g. 145, 3, 80"
                data-testid={`milestone-target-value-${row.slug}`}
                className="bg-transparent"
              />
            </Field>
            <Field label="Unit (optional)">
              <Input
                value={unit}
                onChange={(e) => setUnit(e.target.value)}
                placeholder="lbs · sessions · oz"
                data-testid={`milestone-unit-${row.slug}`}
                className="bg-transparent"
              />
            </Field>
          </>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <Button
          onClick={save}
          disabled={saving}
          data-testid={`milestone-save-${row.slug}`}
          className="rounded-full bg-[hsl(var(--clay))] text-[hsl(var(--paper))] hover:bg-[hsl(var(--clay))]/90"
        >
          {saving ? "Saving…" : m.id ? "Save changes" : "Set milestone"}
        </Button>
        {m.id && (
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={() => setLogOpen((v) => !v)}
              variant="outline"
              data-testid={`milestone-log-toggle-${row.slug}`}
              className="rounded-full"
            >
              <Plus size={14} className="mr-2" /> Log progress
            </Button>
            <Button
              onClick={() => setShareOpen((v) => !v)}
              variant="outline"
              data-testid={`milestone-share-toggle-${row.slug}`}
              className="rounded-full"
              title="Share this milestone with the community"
            >
              <Share2 size={14} className="mr-2" /> Share
            </Button>
          </div>
        )}
      </div>

      {shareOpen && m.id && (
        <div className="p-4 border hairline rounded-md bg-[hsl(var(--terracotta))]/10" data-testid={`milestone-share-form-${row.slug}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--terracotta))] inline-flex items-center gap-2">
            <Share2 size={12} /> Share to community
          </div>
          <p className="mt-2 text-sm text-[hsl(var(--muted-foreground))]">
            A short line will be posted to the feed with your {row.name.toLowerCase()} milestone. Add a note if you'd like.
          </p>
          <Textarea
            value={shareNote}
            onChange={(e) => setShareNote(e.target.value)}
            placeholder="Why does this milestone matter to you?"
            data-testid={`milestone-share-note-${row.slug}`}
            className="mt-3 min-h-[70px] bg-transparent"
          />
          <div className="mt-3 flex justify-end">
            <Button
              onClick={shareToCommunity}
              disabled={sharing}
              data-testid={`milestone-share-submit-${row.slug}`}
              className="rounded-full bg-[hsl(var(--terracotta))] text-[hsl(var(--fg))] hover:bg-[hsl(var(--terracotta))]/90"
            >
              {sharing ? "Sending…" : "Share with the circle"}
            </Button>
          </div>
        </div>
      )}

      {logOpen && (
        <div className="p-4 border hairline rounded-md bg-[hsl(var(--sand))]/30" data-testid={`milestone-log-form-${row.slug}`}>
          <div className={`grid ${row.numeric ? "sm:grid-cols-[auto,1fr,auto]" : "sm:grid-cols-[1fr,auto]"} gap-3 items-end`}>
            {row.numeric && (
              <Field label={`Value${unit ? ` (${unit})` : ""}`}>
                <Input
                  type="number"
                  step="any"
                  value={logValue}
                  onChange={(e) => setLogValue(e.target.value)}
                  placeholder="e.g. 7.5"
                  data-testid={`milestone-log-value-${row.slug}`}
                  className="bg-transparent w-32"
                />
              </Field>
            )}
            <Field label={row.numeric ? "Note" : "Reflection"}>
              <Input
                value={logNote}
                onChange={(e) => setLogNote(e.target.value)}
                placeholder={row.numeric ? "How did today feel?" : "One line — what did today ask of you?"}
                data-testid={`milestone-log-note-${row.slug}`}
                className="bg-transparent"
              />
            </Field>
            <Button
              onClick={logProgress}
              data-testid={`milestone-log-save-${row.slug}`}
              className="rounded-full bg-[hsl(var(--terracotta))] text-[hsl(var(--fg))] hover:bg-[hsl(var(--terracotta))]/90"
            >
              Log
            </Button>
          </div>
        </div>
      )}

      {recent.length > 0 && (
        <div data-testid={`milestone-history-${row.slug}`}>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))] inline-flex items-center gap-2">
            <TrendingUp size={12} /> Recent progress
          </div>
          <ul className="mt-2 divide-y hairline text-sm">
            {recent.map((p, i) => (
              <li key={i} className="py-2 flex items-center justify-between gap-3">
                <div className="text-[hsl(var(--muted-foreground))] text-xs">{p.date}</div>
                <div className="flex-1 truncate">{p.note || <span className="text-[hsl(var(--muted-foreground))]">—</span>}</div>
                {p.value !== null && p.value !== undefined && (
                  <div className="font-display text-base tabular-nums">{p.value}{unit && <span className="text-xs text-[hsl(var(--muted-foreground))] ml-1">{unit}</span>}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--muted-foreground))]">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
